import { AlertTriangle, Loader2, RotateCcw, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  useCancelSubscription,
  useReactivateSubscription,
} from '@/api/stripe'
import type { Subscription } from '@/api/subscription'

interface Props {
  subscription: Subscription
  darkMode?: boolean
}

/**
 * Carte d'annulation/réactivation d'abonnement. 2 états mutuellement exclusifs :
 *
 * - `cancel_at_period_end = false` (abo actif) → bouton "Annuler" en danger
 *   zone. AlertDialog de confirmation avec explication détaillée.
 * - `cancel_at_period_end = true` (annulation programmée) → bandeau info +
 *   bouton "Réactiver" pour annuler l'annulation.
 *
 * Stripe garde l'abonnement actif jusqu'à `current_period_end`. Le `useEffect`
 * d'invalidation force le refresh du status après mutation.
 */
export function CancelSubscriptionCard({ subscription, darkMode = false }: Props) {
  const { t, i18n } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const cancelMutation = useCancelSubscription()
  const reactivateMutation = useReactivateSubscription()

  const periodEnd = formatDate(subscription.current_period_end, locale || i18n.language)

  if (subscription.cancel_at_period_end) {
    return (
      <div
        className={cn(
          'rounded-2xl border p-5',
          darkMode
            ? 'border-amber-800/40 bg-amber-500/5'
            : 'border-amber-200 bg-amber-50',
        )}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <AlertTriangle
              className={cn(
                'h-5 w-5 mt-0.5 flex-shrink-0',
                darkMode ? 'text-amber-400' : 'text-amber-600',
              )}
            />
            <div className="min-w-0">
              <p
                className={cn(
                  'text-sm font-semibold',
                  darkMode ? 'text-amber-200' : 'text-amber-900',
                )}
              >
                {t(
                  'profile.billing.cancelScheduledTitle',
                  'Annulation programmée',
                )}
              </p>
              <p
                className={cn(
                  'text-xs mt-1',
                  darkMode ? 'text-amber-300/80' : 'text-amber-800',
                )}
              >
                {t('profile.billing.cancelScheduledDescription', {
                  date: periodEnd,
                  defaultValue:
                    "Votre abonnement reste actif jusqu'au {{date}}. Aucun renouvellement automatique.",
                })}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() =>
              reactivateMutation.mutate(undefined, {
                onSuccess: () =>
                  toast.success(
                    t(
                      'profile.billing.reactivateSuccess',
                      'Abonnement réactivé',
                    ),
                  ),
                onError: () =>
                  toast.error(
                    t(
                      'profile.billing.reactivateError',
                      'Impossible de réactiver',
                    ),
                  ),
              })
            }
            disabled={reactivateMutation.isPending}
            className={darkMode ? 'border-amber-700/50' : 'border-amber-300'}
          >
            {reactivateMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-2" />
            )}
            {t('profile.billing.reactivate', 'Réactiver')}
          </Button>
        </div>
      </div>
    )
  }

  // État actif : zone danger avec confirmation
  return (
    <div
      className={cn(
        'rounded-2xl border p-5',
        darkMode
          ? 'border-red-900/40 bg-red-500/5'
          : 'border-red-200 bg-red-50/40',
      )}
    >
      <div className="flex items-start gap-3">
        <XCircle
          className={cn(
            'h-5 w-5 mt-0.5 flex-shrink-0',
            darkMode ? 'text-red-400' : 'text-red-600',
          )}
        />
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'text-sm font-semibold',
              darkMode ? 'text-red-200' : 'text-red-900',
            )}
          >
            {t('profile.billing.cancelTitle', 'Annuler mon abonnement')}
          </p>
          <p
            className={cn(
              'text-xs mt-1 mb-4',
              darkMode ? 'text-red-300/80' : 'text-red-800',
            )}
          >
            {t(
              'profile.billing.cancelDescription',
              "Vos numéros et votre accès restent actifs jusqu'à la fin de la période en cours. Aucun remboursement.",
            )}
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm">
                {t('profile.billing.cancelButton', 'Annuler mon abonnement')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent
              className={cn(darkMode && 'bg-slate-900 border-slate-700')}
            >
              <AlertDialogHeader>
                <AlertDialogTitle className={darkMode ? 'text-white' : ''}>
                  {t(
                    'profile.billing.cancelConfirmTitle',
                    'Confirmer l\'annulation ?',
                  )}
                </AlertDialogTitle>
                <AlertDialogDescription
                  className={darkMode ? 'text-slate-400' : ''}
                >
                  {t('profile.billing.cancelConfirmDescription', {
                    date: periodEnd,
                    defaultValue:
                      "Votre abonnement restera actif jusqu'au {{date}}. Après cette date, vos numéros seront libérés et vos données conservées 30 jours.",
                  })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  className={darkMode ? 'border-slate-700' : ''}
                  disabled={cancelMutation.isPending}
                >
                  {t('common.cancel', 'Annuler')}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    cancelMutation.mutate(undefined, {
                      onSuccess: () =>
                        toast.success(
                          t(
                            'profile.billing.cancelSuccess',
                            'Annulation programmée',
                          ),
                        ),
                      onError: () =>
                        toast.error(
                          t(
                            'profile.billing.cancelError',
                            'Impossible d\'annuler',
                          ),
                        ),
                    })
                  }
                  disabled={cancelMutation.isPending}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {cancelMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  {t('profile.billing.cancelConfirm', 'Oui, annuler')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  )
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(
      locale === 'fr' ? 'fr-CA' : 'en-CA',
      { year: 'numeric', month: 'long', day: 'numeric' },
    )
  } catch {
    return iso
  }
}
